/**
 * Lead Conversion analytics tab.
 *
 * Horizontal funnel of pipeline stages + headline KPIs. Extracted from
 * `pages/Analytics.js` to mitigate the visual-edits Babel plugin recursion
 * crash on large modules.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRIMARY = '#0BA888';

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;

const FunnelRow = ({ stage, widthPct, showConv }) => {
  const critical = showConv && stage.conversionPct === 0;
  const slug = stage.name.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex items-center gap-3" data-testid={`funnel-stage-${slug}`}>
      <div className="w-[110px] text-right text-xs text-slate-700">{stage.name}</div>
      <div className="flex-1 relative h-9 rounded-md bg-slate-50">
        <div
          className="h-full rounded-md flex items-center px-3"
          style={{ width: `${Math.max(widthPct, 4)}%`, backgroundColor: PRIMARY, transition: 'width 280ms ease' }}
        >
          <span className="text-sm font-semibold text-white">{stage.count}</span>
        </div>
      </div>
      <div className="w-[130px] text-left text-xs">
        {showConv ? <ConversionLabel critical={critical} pct={stage.conversionPct} /> : null}
      </div>
    </div>
  );
};

const ConversionLabel = ({ critical, pct }) => {
  if (critical) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[10px] font-semibold uppercase"
        data-testid="critical-gap-badge"
      >
        Critical gap
      </span>
    );
  }
  return <span className="font-mono text-slate-500">{pct}%</span>;
};

const HeadlineCard = ({ label, value, subValue, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-lg font-semibold text-slate-900 mt-1 truncate">{value}</div>
    {subValue ? <div className="text-[10px] text-slate-400 mt-0.5">{subValue}</div> : null}
  </Card>
);

const LeadConversionTab = () => {
  const [data, setData] = useState({ stages: [], overallConversionPct: 0, avgCloseDays: null, avgDealValue: 0, leadConversion: null, quoteBooking: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/analytics/lead-conversion?period=all`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => toast.error('Failed to load lead conversion'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const maxCount = useMemo(
    () => Math.max(1, ...data.stages.map((s) => s.count || 0)),
    [data.stages],
  );

  // Hoist to identifiers — see DestinationsTab note on Babel plugin recursion.
  const stages = data.stages;
  const overallConversionPct = data.overallConversionPct;
  const avgDealValue = data.avgDealValue;

  // Amber banner when New→Contacted = 0%. Biggest predictor of a broken motion.
  const contactedStage = stages.find((s) => s.name === 'Contacted');
  const contactRateZero = contactedStage && contactedStage.conversionPct === 0;
  const avgCloseLabel = (data.avgCloseDays === null || data.avgCloseDays === undefined) ? '—' : `${data.avgCloseDays} days`;
  const leadConv = data.leadConversion;
  const quoteBooking = data.quoteBooking;

  return (
    <div className="space-y-5" data-testid="lead-conversion-tab">
      {contactRateZero ? (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg" data-testid="conversion-amber-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Contact rate is 0% — leads aren't being followed up. This is the single biggest gap in your funnel right now.
        </div>
      ) : null}

      {/* Headline conversion ratios */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <HeadlineCard
          testid="ratio-lead-conversion"
          label="Lead Conversion Ratio"
          value={`${leadConv ? leadConv.ratioPct : 0}%`}
          subValue={leadConv ? `${leadConv.converted} converted / ${leadConv.total} total leads` : 'converted ÷ total leads'}
        />
        <HeadlineCard
          testid="ratio-quote-booking"
          label="Quote → Booking Ratio"
          value={`${quoteBooking ? quoteBooking.ratioPct : 0}%`}
          subValue={quoteBooking ? `${quoteBooking.convertedBookings} bookings / ${quoteBooking.quotesSent} quotes sent` : 'confirmed bookings ÷ quotes sent'}
        />
      </div>

      <Card className="p-5" data-testid="conversion-funnel">
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-2">
            {stages.map((s, idx) => (
              <FunnelRow key={s.name} stage={s} widthPct={(s.count / maxCount) * 100} showConv={idx > 0} />
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <HeadlineCard testid="conv-overall" label="Overall conversion" value={`${overallConversionPct}%`} />
        <HeadlineCard testid="conv-avg-close" label="Avg close time" value={avgCloseLabel} />
        <HeadlineCard testid="conv-avg-deal" label="Avg deal value" value={fmtINR(avgDealValue)} />
      </div>
    </div>
  );
};

export default LeadConversionTab;
